/**
 * Theme preference primitives. Pure, framework-free helpers shared by the
 * pre-paint inline script (conceptually, see index.html), the ThemeProvider,
 * and the settings control. The platform defaults to `auto` (follow the OS);
 * an explicit `light` / `dark` choice overrides it.
 */

/** What the user picks. `auto` tracks the OS via prefers-color-scheme. */
export type ThemePreference = 'light' | 'dark' | 'auto';

/** What actually gets written to `data-theme` (auto is resolved away). */
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key holding the {@link ThemePreference}. */
export const THEME_STORAGE_KEY = 'holito-theme';

/** Narrowing guard for stored / external values. */
export const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'auto';

/** Resolve a preference to a concrete theme given the current OS preference. */
export const resolveTheme = (
  preference: ThemePreference,
  systemDark: boolean,
): ResolvedTheme => (preference === 'auto' ? (systemDark ? 'dark' : 'light') : preference);

/** Read the saved preference, defaulting to `auto` (and surviving a locked-down or absent localStorage). */
export const getStoredThemePreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
};

/** Persist the preference. Storage failures (private mode, quota) are swallowed: theme is non-critical state. */
export const storeThemePreference = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // ignore: a missing preference simply falls back to auto next load
  }
};

/** Whether the OS currently prefers a dark colour scheme. Defaults to dark when matchMedia is unavailable. */
export const systemPrefersDark = (): boolean => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return true;
  }
};

/** Apply a resolved theme to the document root (drives every `data-theme` CSS rule). */
export const applyTheme = (theme: ResolvedTheme): void => {
  document.documentElement.setAttribute('data-theme', theme);
};
